import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, RefreshCw, Search, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CatalogPanel, MaintenancePanel } from "@/components/teacher/SiteAdmin";

const CHUNK = 512 * 1024;
const toB64 = (buf) => { let s = ""; const b = new Uint8Array(buf); for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000)); return btoa(s); };

const BackupPanel = () => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const download = async () => {
    try {
      const r = await api.get("/dev/backup", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fchem-yedek-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Yedek indirildi");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async () => {
    const f = file;
    setFile(null);
    const id = Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
    const total = Math.max(1, Math.ceil(f.size / CHUNK));
    try {
      for (let i = 0; i < total; i += 1) {
        setProgress(Math.round((i / total) * 100));
        await api.post("/dev/restore/chunk", { upload_id: id, index: i, data: toB64(await f.slice(i * CHUNK, (i + 1) * CHUNK).arrayBuffer()) });
      }
      setProgress(100);
      const { data } = await api.post("/dev/restore/commit", { upload_id: id, total });
      toast.success(`Geri yüklendi: ${data.collections} koleksiyon, ${data.documents} kayıt`);
    } catch (e) { toast.error(errMsg(e)); } finally { setProgress(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="panel p-5 space-y-3" data-testid="backup-panel">
      <h2 className="font-display text-2xl font-bold flex items-center gap-2"><DatabaseBackup className="h-6 w-6 text-[#FACC15]" />Yedekleme ve geri yükleme</h2>
      <p className="text-sm text-slate-400">Tüm veriyi (öğretmenler, havuzlar, sorular, etkinlikler, oyunlar, analizler, rekorlar, loglar) tek bir dosyaya indir. Geri yüklemede mevcut verinin yerine dosyadaki veri geçer.</p>
      <div className="flex flex-wrap gap-3 items-center">
        <Button onClick={download} className="font-bold" data-testid="backup-download"><Download className="h-4 w-4 mr-2" />Yedek indir</Button>
        <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="restore-file-input" />
        <Button variant="outline" disabled={progress !== null} onClick={() => fileRef.current?.click()} data-testid="restore-pick"><Upload className="h-4 w-4 mr-2" />Yedekten geri yükle</Button>
        {progress !== null && <span className="text-sm text-[#FACC15]" data-testid="restore-progress">Yükleniyor… %{progress}</span>}
      </div>
      <ConfirmDialog open={Boolean(file)} onOpenChange={(o) => { if (!o) { setFile(null); if (fileRef.current) fileRef.current.value = ""; } }} title="Yedekten geri yükle"
        description={`"${file?.name || ""}" dosyasındaki veri mevcut verinin yerine geçecek. Başlamadan önce güncel bir yedek indirmeniz önerilir. Devam edilsin mi?`}
        confirmText="Geri yükle" onConfirm={restore} />
    </div>
  );
};

const PURGE = [["7", "7 günden eski"], ["30", "30 günden eski"], ["90", "90 günden eski"], ["all", "Tüm loglar"]];

const fmt = (iso) => new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

const LogRow = ({ l, i }) => {
  const failed = l.status >= 400;
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`log-row-${i}`}>
      <td className="py-2 pr-4 text-slate-400 whitespace-nowrap font-mono-x text-xs">{fmt(l.at)}</td>
      <td className="py-2 pr-4"><p className="font-semibold">{l.actor_name}</p><p className="text-xs text-slate-500">{l.actor_email}</p></td>
      <td className="py-2 pr-4 font-semibold">{l.action}</td>
      <td className="py-2 pr-4 text-slate-300 max-w-xs truncate" title={l.target}>{l.target || "—"}</td>
      <td className="py-2 pr-4">
        <span className={`text-xs rounded-full px-2 py-0.5 font-bold ${failed ? "bg-red-500/20 text-red-300" : "bg-[#22C55E]/15 text-[#4ADE80]"}`}>{failed ? `Hata ${l.status}` : "Başarılı"}</span>
      </td>
      <td className="py-2 text-xs text-slate-500 font-mono-x">{l.ip || ""}</td>
    </tr>
  );
};

export default function LogsPage() {
  const [data, setData] = useState({ logs: [], actors: [] });
  const [q, setQ] = useState("");
  const [actor, setActor] = useState("all");
  const [status, setStatus] = useState("all");
  const [purge, setPurge] = useState("30");
  const [confirmPurge, setConfirmPurge] = useState(false);
  const load = useCallback(() => {
    api.get("/audit", { params: { q, actor: actor === "all" ? "" : actor, status, limit: 500 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, actor, status]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const purgeLogs = async () => {
    const before = purge === "all" ? undefined : new Date(Date.now() - Number(purge) * 86400000).toISOString();
    try {
      const { data: r } = await api.delete("/dev/audit", { params: before ? { before } : {} });
      toast.success(`${r.deleted} log silindi`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="logs-page">
      <BackupPanel />
      <div>
        <p className="eyebrow">Geliştirici</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Eylem Logları</h1>
        <p className="text-slate-400 mt-1">Kimin, ne zaman, hangi eylemi yaptığı. En yeni kayıtlar üstte.</p>
      </div>
      <MaintenancePanel />
      <CatalogPanel />
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute z-10 left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Eylem, hedef ya da kişi ara…" className="pl-9 h-11" data-testid="logs-search" />
        </div>
        <Select value={actor} onValueChange={setActor}>
          <SelectTrigger className="h-11 md:w-64" data-testid="logs-actor"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm kişiler</SelectItem>
            {data.actors.map((a) => <SelectItem key={a.email} value={a.email}>{a.name} ({a.email})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 md:w-44" data-testid="logs-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm sonuçlar</SelectItem>
            <SelectItem value="ok">Başarılı</SelectItem>
            <SelectItem value="error">Hatalı</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} className="h-11" data-testid="logs-refresh"><RefreshCw className="h-4 w-4 mr-2" />Yenile</Button>
        <Select value={purge} onValueChange={setPurge}>
          <SelectTrigger className="h-11 md:w-44" data-testid="logs-purge-range"><SelectValue /></SelectTrigger>
          <SelectContent>{PURGE.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="ghost" className="h-11 text-red-400" onClick={() => setConfirmPurge(true)} data-testid="logs-purge"><Trash2 className="h-4 w-4 mr-2" />Logları sil</Button>
      </div>
      <ConfirmDialog open={confirmPurge} onOpenChange={setConfirmPurge} title="Logları sil" description={`${PURGE.find((p) => p[0] === purge)[1]} loglar kalıcı olarak silinsin mi?`} confirmText="Sil" onConfirm={() => { setConfirmPurge(false); purgeLogs(); }} />
      <div className="panel p-4 overflow-x-auto">
        {data.logs.length === 0 ? <p className="text-slate-400 p-6" data-testid="logs-empty">Kayıt yok.</p> : (
          <table className="w-full text-sm" data-testid="logs-table">
            <thead><tr className="text-left eyebrow text-[0.65rem]"><th className="pb-2 pr-4">Zaman</th><th className="pb-2 pr-4">Kişi</th><th className="pb-2 pr-4">Eylem</th><th className="pb-2 pr-4">Hedef</th><th className="pb-2 pr-4">Sonuç</th><th className="pb-2">IP</th></tr></thead>
            <tbody>{data.logs.map((l, i) => <LogRow key={`${l.at}-${i}`} l={l} i={i} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
